import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useCustomErrors } from "go-form/hooks";
import AccessTokenSessionService from "go-security/services/accessTokenSessionService";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../services/Api/Organization/apiOrganization";
import {
	ApplicationApi,
	ApplicationProvider,
	ApplicationTemplateApi,
	ApplicationUpdateApi,
} from "../../../../../../../../services/Api/Organization/types";
import BetasiForm from "../Providers/Betasi/BetasiForm";
import GoHotelIntegrationForm from "../Providers/GoHotelIntegration/GoHotelIntegrationForm";
import HardRockCafeForm from "../Providers/HardRockCafe/HardRockCafeForm";
import HolidayPmsInstalModal from "../Providers/HolidayPms/HolidayPmsInstalModal";
import GenericHotelForm from "../Providers/HotelAppForm/GenericHotelForm";
import HotelgramForm from "../Providers/Hotelgram/HotelgramForm";
import HotellionInstallationForm from "../Providers/Hotellion/HotellionInstallationForm";
import HotresForm from "../Providers/Hotres/HotresForm";
import InforStarlightInstallationForm from "../Providers/InforStarlight/InforStarlightInstallationForm";
import KWHotelForm from "../Providers/KWHotel/KWHotelForm";
import MewsPreInstallationForm from "../Providers/Mews/MewsPreInstallationForm";
import MultiLocationInstallationForm from "../Providers/MultiLocation/MultiLocationInstallationForm";
import NfHotelInstallationForm from "../Providers/NfHotel/NfHotelInstallationForm";
import WoltDriveInstallationForm from "../Providers/WoltDrive/WoltDriveInstallationForm";

interface Props {
	show: boolean;
	onHide: () => void;
	appConfig: ApplicationTemplateApi;
	handleUpdateNewInstalledApp?: (app: ApplicationApi) => void;
}

const InstallationAppForm = ({ onHide, show, appConfig, handleUpdateNewInstalledApp }: Props) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState<boolean>(false);
	const history = useHistory();
	const { addFlash, addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const form = useForm<ApplicationUpdateApi>({
		criteriaMode: "all",
		defaultValues: {},
	});
	const { handleSubmit, setError, watch } = form;
	const appsInstalledViaToken: ApplicationProvider[] = [ApplicationProvider.MEWS];
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const renderInstallationForm = () => {
		switch (appConfig.provider) {
			case ApplicationProvider.LOCATIONS:
				return <MultiLocationInstallationForm form={form} />;
			case ApplicationProvider.GOHOTELINTEGRATION:
				return <GoHotelIntegrationForm form={form} />;
			case ApplicationProvider.MEWS:
				return <MewsPreInstallationForm form={form} />;
			case ApplicationProvider.HOTELLION:
				return <HotellionInstallationForm form={form} />;
			case ApplicationProvider.INFOR_STARLIGHT:
				return <InforStarlightInstallationForm form={form} />;
			case ApplicationProvider.HOLIDAY_PMS:
				return <HolidayPmsInstalModal form={form} />;
			case ApplicationProvider.HOTELGRAM:
				return <HotelgramForm form={form} />;
			case ApplicationProvider.HOTRES:
				return <HotresForm form={form} />;
			case ApplicationProvider.CHART:
			case ApplicationProvider.M_HOTEL:
				return <GenericHotelForm form={form} />;
			case ApplicationProvider.NFHOTEL:
				return <NfHotelInstallationForm form={form} />;
			case ApplicationProvider.KWHOTEL:
				return <KWHotelForm form={form} />;
			case ApplicationProvider.WOLT_DRIVE:
				return <WoltDriveInstallationForm form={form} />;
			case ApplicationProvider.HARD_ROCK_CAFE:
				return <HardRockCafeForm form={form} setErrors={setErrors} />;
			case ApplicationProvider.BETASI:
			case ApplicationProvider.BETASI_V2:
				return <BetasiForm form={form} setErrors={setErrors} provider={appConfig.provider} />;
			default:
				return <></>;
		}
	};

	const installViaToken = async (data: ApplicationUpdateApi) => {
		const accessToken = AccessTokenSessionService.get();
		return apiOrganization.installApplicationPost(appConfig.provider, accessToken, data);
	};

	const shouldInstallViaToken = appsInstalledViaToken.includes(appConfig.provider);

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) return;

		setLoading(true);
		try {
			const res = shouldInstallViaToken
				? await installViaToken(data)
				: await apiOrganization.installApps(appConfig.provider, data);
			addSuccessFlash(t("lib:common.flash.completed"));
			history.push(`/${organization.id}/apps/active/${res.id}`);
			handleUpdateNewInstalledApp?.(res);
			onHide();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const isSubmitButtonDisabled = () => {
		switch (appConfig.provider) {
			case ApplicationProvider.MEWS:
				return !watch("settings.accesstoken");
			default:
				return false;
		}
	};

	return (
		<Modal show={show} onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>
					{`${t("modules.app.field.install.title")} ${t(`enums.apps.providers.${appConfig.provider}`)}`}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>{renderInstallationForm()}</Modal.Body>
			<Modal.Footer>
				<ButtonLoading loading={loading} type="submit" onClick={onSubmit} disabled={isSubmitButtonDisabled()}>
					{t("lib:common.action.install")}
				</ButtonLoading>
				<Button variant="light" onClick={onHide}>
					{t("lib:common.action.cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default InstallationAppForm;
